import * as THREE from 'three';

// Screen-space galaxies visibly recover as the liberation wave crosses them.
export const createCoreFinaleSky = () => {
  const material = new THREE.ShaderMaterial({
    depthTest: false,
    depthWrite: false,
    uniforms: {
      time: { value: 0 },
      aspect: { value: 1 },
      restoration: { value: 0 },
      ignition: { value: 0 },
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = vec4(position.xy, 0.9999, 1.0);
      }
    `,
    fragmentShader: `
      varying vec2 vUv;
      uniform float time;
      uniform float aspect;
      uniform float restoration;
      uniform float ignition;
      float hash(vec2 p) {
        return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
      }
      mat2 rotate(float a) {
        return mat2(cos(a), -sin(a), sin(a), cos(a));
      }
      float stars(vec2 p, float density) {
        vec2 cell = floor(p * density);
        vec2 local = fract(p * density) - 0.5;
        float seed = hash(cell);
        vec2 offset = vec2(hash(cell + 4.2), hash(cell - 7.1)) * 0.7 - 0.35;
        return exp(-dot(local - offset, local - offset) * 680.0)
          * step(0.96, seed) * (0.75 + sin(time * 0.5 + seed * 40.0) * 0.25);
      }
      vec3 galaxy(vec2 p, vec2 center, float size, float tilt, float phase, vec3 tint) {
        vec2 d = rotate(tilt) * (p - center) / size;
        d.y *= 1.8;
        float r = length(d);
        if (r > 1.8) return vec3(0.0);
        d = rotate(time * 0.025 + phase) * d;
        float angle = atan(d.y, d.x + 0.00001);
        float grain = hash(floor(d * 140.0));
        float arms = pow(0.5 + 0.5 * cos(angle * 2.0 - r * 10.0
          + sin(r * 5.0 + phase) * 0.6), 3.0);
        float dust = (0.15 + arms * 1.8) * exp(-r * 2.3)
          * (0.65 + grain * 0.45) * (1.0 - smoothstep(1.0, 1.8, r));
        vec3 light = mix(tint, vec3(0.22, 0.68, 1.0), grain * 0.5) * dust;
        light += vec3(1.0, 0.87, 0.64) * exp(-r * 24.0) * 1.6;
        light += vec3(0.8, 0.95, 1.0) * stars(d, 24.0) * exp(-r * 2.0);
        return light;
      }
      void main() {
        vec2 p = (vUv - 0.5) * vec2(aspect, 1.0);
        float reach = restoration * (length(vec2(aspect, 1.0)) * 0.65 + 0.15);
        float healed = 1.0 - smoothstep(reach - 0.05, reach + 0.1, length(p));
        healed *= smoothstep(0.0, 0.04, restoration);
        vec2 broken = p;
        float scan = hash(vec2(floor(vUv.y * 85.0), floor(time * 7.0)));
        broken.x += (scan - 0.5) * 0.035 * (1.0 - healed);
        float span = clamp(aspect, 0.65, 1.4);
        vec3 worlds = galaxy(broken, vec2(-aspect * 0.3, 0.12), span * 0.34,
          -0.4, 0.0, vec3(0.55, 0.23, 0.95));
        worlds += galaxy(broken, vec2(aspect * 0.3, -0.15), span * 0.3,
          0.4, 2.0, vec3(0.2, 0.65, 1.0));
        worlds += galaxy(broken, vec2(aspect * 0.26, 0.33), span * 0.14,
          -0.2, 4.0, vec3(0.9, 0.46, 0.63));
        worlds += galaxy(broken, vec2(-aspect * 0.24, -0.32), span * 0.16,
          0.6, 1.2, vec3(0.25, 0.95, 0.8));
        worlds += galaxy(broken, vec2(0.0, 0.36), span * 0.12,
          -0.6, 3.0, vec3(0.65, 0.6, 1.0));
        float starfield = stars(p, 75.0) + stars(p, 135.0) * 0.5;
        vec3 sky = vec3(0.004, 0.009, 0.024)
          + vec3(0.008, 0.025, 0.045) * exp(-length(p) * 1.4);
        vec3 corruption = vec3(0.035, 0.004, 0.025)
          + vec3(0.34, 0.025, 0.11) * dot(worlds, vec3(0.33)) * 0.45;
        corruption *= 0.7 + scan * 0.3;
        vec3 color = mix(corruption, sky + worlds + starfield * vec3(0.65, 0.85, 1.0), healed);
        float wave = exp(-pow((length(p) - reach) * 38.0, 2.0))
          * sin(restoration * 3.14159265);
        color += vec3(0.7, 1.0, 0.9) * wave * 0.65;
        color += vec3(0.8, 0.65, 0.35) * ignition * exp(-length(p) * 12.0) * 0.35;
        color *= 1.0 - smoothstep(0.3, 1.0, length(vUv - 0.5)) * 0.4;
        gl_FragColor = vec4(color, 1.0);
      }
    `,
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
  mesh.frustumCulled = false;
  mesh.renderOrder = -100;
  return {
    mesh,
    resize(aspect) {
      material.uniforms.aspect.value = aspect;
    },
    update(time, restoration, ignition) {
      material.uniforms.time.value = time;
      material.uniforms.restoration.value = restoration;
      material.uniforms.ignition.value = ignition;
    },
  };
};
